import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaFilter } from "react-icons/fa";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import { fetchUsersWithEvents, fetchAllUsers, toggleUserStatus, setEventDisabled } from "../../../../utils/api/adminApi";
import "../css/SystemUsers.css";
import SystemUsersTable from "../components/SystemUsersTable.jsx";
import Pagination from "../../../../components/Pagination.jsx";

const SystemUsers = () => {
  const nav = useNavigate();
  const [activeTable, setActiveTable] = useState("Organiser");
  const [generalUsers, setGeneralUsers] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrganisers, setFilteredOrganisers] = useState([]);
  const [filteredGeneralUsers, setFilteredGeneralUsers] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [showCustomDates, setShowCustomDates] = useState(false);

  const getDateOptions = () => {
    const today = new Date();
    const oneMonthFromNow = new Date(today);
    oneMonthFromNow.setMonth(today.getMonth() + 1);
    
    const threeMonthsFromNow = new Date(today);
    threeMonthsFromNow.setMonth(today.getMonth() + 3);
    
    const oneYearFromNow = new Date(today);
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    return [
      { value: '', label: 'All Dates' },
      { value: '1month', label: `Within 1 Month (${today.toLocaleDateString()} - ${oneMonthFromNow.toLocaleDateString()})` },
      { value: '3months', label: `Within 3 Months (${today.toLocaleDateString()} - ${threeMonthsFromNow.toLocaleDateString()})` },
      { value: '1year', label: `Within 1 Year (${today.toLocaleDateString()} - ${oneYearFromNow.toLocaleDateString()})` },
      { value: 'custom', label: 'Custom Date Range' }
    ];
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    const fetchOrganisers = async () => {
      try {
        const data = await fetchUsersWithEvents();
        setOrganisers(data);
      } catch (error) {
        console.error("Error fetching organisers:", error);
        setOrganisers([]);
      }
    };

    const fetchGeneralUsers = async () => {
      try {
        const data = await fetchAllUsers();
        setGeneralUsers(data);
      } catch (error) {
        console.error("Error fetching general users:", error);
        setGeneralUsers([]);
      }
    };

    // Add delay between API calls to prevent rate limiting
    const fetchData = async () => {
      setLoading(true);
      await fetchOrganisers();
      // Wait 500ms before making the second request
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchGeneralUsers();
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Filter organisers
    if (organisers.length > 0) {
      let filtered = organisers.filter((organiser) => {
        const fullName = `${organiser.firstname || ''} ${organiser.surname || ''}`.toLowerCase();
        const email = (organiser.email || '').toLowerCase();
        const eventName = (organiser.event_name || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          eventName.includes(searchLower)
        );
      });

      // Apply status filter for events
      if (statusFilter) {
        filtered = filtered.filter((organiser) => {
          const status = organiser.event_status === true || organiser.event_status === 'Approved' ? 'Approved' : 
                        organiser.event_status === false || organiser.event_status === 'Rejected' ? 'Rejected' : 'Pending';
          return status.toLowerCase() === statusFilter.toLowerCase();
        });
      }

      // Apply date filter for events
      if (dateFilter) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (dateFilter === '1month') {
          const oneMonthFromNow = new Date(today);
          oneMonthFromNow.setMonth(today.getMonth() + 1);
          filtered = filtered.filter((organiser) => {
            const eventDateStr = organiser.startdate || organiser.StartDate || organiser.date || organiser.start_date;
            if (!eventDateStr) return false;
            const eventDate = new Date(eventDateStr);
            return eventDate >= today && eventDate <= oneMonthFromNow;
          });
        } else if (dateFilter === '3months') {
          const threeMonthsFromNow = new Date(today);
          threeMonthsFromNow.setMonth(today.getMonth() + 3);
          filtered = filtered.filter((organiser) => {
            const eventDateStr = organiser.startdate || organiser.StartDate || organiser.date || organiser.start_date;
            if (!eventDateStr) return false;
            const eventDate = new Date(eventDateStr);
            return eventDate >= today && eventDate <= threeMonthsFromNow;
          });
        } else if (dateFilter === '1year') {
          const oneYearFromNow = new Date(today);
          oneYearFromNow.setFullYear(today.getFullYear() + 1);
          filtered = filtered.filter((organiser) => {
            const eventDateStr = organiser.startdate || organiser.StartDate || organiser.date || organiser.start_date;
            if (!eventDateStr) return false;
            const eventDate = new Date(eventDateStr);
            return eventDate >= today && eventDate <= oneYearFromNow;
          });
        } else if (dateFilter === 'custom' && customFromDate && customToDate) {
          const fromDate = new Date(customFromDate);
          const toDate = new Date(customToDate);
          filtered = filtered.filter((organiser) => {
            const eventDateStr = organiser.startdate || organiser.StartDate || organiser.date || organiser.start_date;
            if (!eventDateStr) return false;
            const eventDate = new Date(eventDateStr);
            return eventDate >= fromDate && eventDate <= toDate;
          });
        }
      }

      setFilteredOrganisers(filtered);
    } else {
      setFilteredOrganisers([]);
    }

    // Filter general users
    if (generalUsers.length > 0) {
      let filtered = generalUsers.filter((user) => {
        if (user.role === "Admin") return false;

        const fullName = `${user.firstname || ''} ${user.surname || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          role.includes(searchLower)
        );
      });

      // Apply status filter for users
      if (statusFilter) {
        filtered = filtered.filter((user) => {
          const status = user.is_disabled === 1 ? 'disabled' : 'enabled';
          return status === statusFilter.toLowerCase();
        });
      }

      setFilteredGeneralUsers(filtered);
    } else {
      setFilteredGeneralUsers([]);
    }
    
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchTerm, organisers, generalUsers, dateFilter, statusFilter, customFromDate, customToDate]);

  useEffect(() => {
    setCurrentPage(1);
    // Clear date filter when switching to Users tab
    if (activeTable === "General Users") {
      setDateFilter("");
      setShowCustomDates(false);
      setCustomFromDate("");
      setCustomToDate("");
    }
  }, [activeTable]);

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setStatusFilter("");
    setShowCustomDates(false);
    setCustomFromDate("");
    setCustomToDate("");
  };

  const hasActiveFilters = searchTerm || dateFilter || statusFilter;

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setLoading(true);
      const response = await toggleUserStatus(userId, currentStatus);

      const organisersData = await fetchUsersWithEvents();
      setOrganisers(organisersData);

      const allUsersData = await fetchAllUsers();
      setGeneralUsers(allUsersData);

      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling user status:", error);
      setSuccessMessage("Failed to update user status. Please try again.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEventDisabled = async (eventId, currentDisabled) => {
    if (eventId === undefined || eventId === null) {
      setSuccessMessage("Unable to update: event id missing. Please refresh the page.");
      setTimeout(() => setSuccessMessage(""), 3000);
      return;
    }
    try {
      const response = await setEventDisabled(eventId, !currentDisabled);
      const newDisabled = response?.disabled ?? !currentDisabled;

      // Update only the affected event row(s) in place — no full-table reload
      setOrganisers((prev) =>
        prev.map((organiser) =>
          organiser.event_id === eventId
            ? { ...organiser, disabled: newDisabled }
            : organiser
        )
      );

      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling event visibility:", error);
      setSuccessMessage("Failed to update event visibility. Please try again.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  return (
    <div className="system-users-page">
      <GlobalHeader
        title="System Users"
        subtitle="Manage user accounts"
        backTo="/admin/dashboard"
        logoutTo="/"
      />

      <main className="system-users-main">
        {successMessage && (
          <div className="system-users-success-message">
            {successMessage}
          </div>
        )}
        <section className="system-users-search">
          <div className="system-users-search__inner">
            <input
              type="text"
              placeholder={activeTable === "Organiser" ? "Search events..." : "Search users..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="system-users-search__input"
            />
            <button
              type="button"
              className="system-users-filter-button"
              onClick={() => setShowFilters(true)}
            >
              <FaFilter style={{ marginRight: 5 }} /> Filters
            </button>
          </div>
        </section>

        <section className="system-users-tabs">
          <button
            type="button"
            className={`system-users-tab-button ${
              activeTable === "Organiser" ? "system-users-tab-button--active" : ""
            }`}
            onClick={() => setActiveTable("Organiser")}
          >
            Events
          </button>
          <button
            type="button"
            className={`system-users-tab-button ${
              activeTable === "General Users" ? "system-users-tab-button--active" : ""
            }`}
            onClick={() => setActiveTable("General Users")}
          >
            Users
          </button>
        </section>

        <section className="system-users-table-section">
          <SystemUsersTable
            loading={loading}
            activeTable={activeTable}
            organisers={filteredOrganisers}
            generalUsers={filteredGeneralUsers}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onViewOrganiser={(userId) =>
              nav("/viewingorganiser", { state: { userId } })
            }
            onViewCustomer={(userId) =>
              nav("/viewingcustomer", { state: { userId } })
            }
            onToggleStatus={handleToggleUserStatus}
            onToggleEventDisabled={handleToggleEventDisabled}
          />
          
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(
              (activeTable === "Organiser" ? filteredOrganisers : filteredGeneralUsers).length / itemsPerPage
            )}
            totalItems={(activeTable === "Organiser" ? filteredOrganisers : filteredGeneralUsers).length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </section>
      </main>

      {showFilters && (
        <div
          className="system-users-overlay"
          onClick={() => setShowFilters(false)}
        >
          <div
            className="system-users-filter-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="system-users-filter-close"
              onClick={() => setShowFilters(false)}
            >
              ✕
            </button>

            <h2 className="system-users-filter-title">Filters</h2>

            <div className="system-users-filter-section">
              {activeTable === "Organiser" && (
                <div className="system-users-filter-group">
                  <label className="system-users-filter-label">Date</label>
                  <select
                    className="system-users-filter-select"
                    value={dateFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateFilter(value);
                      setShowCustomDates(value === 'custom');
                    }}
                  >
                    {dateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {showCustomDates && (
                    <div style={{ marginTop: 15 }}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', marginBottom: 5, fontSize: '14px', fontWeight: '500' }}>
                          From Date:
                        </label>
                        <input
                          type="date"
                          className="system-users-filter-date"
                          value={customFromDate}
                          onChange={(e) => setCustomFromDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontSize: '14px', fontWeight: '500' }}>
                          To Date:
                        </label>
                        <input
                          type="date"
                          className="system-users-filter-date"
                          value={customToDate}
                          onChange={(e) => setCustomToDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="system-users-filter-group">
                <label className="system-users-filter-label">
                  Status ({activeTable === "Organiser" ? "Event" : "User"})
                </label>
                <select
                  className="system-users-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {activeTable === "Organiser" ? (
                    <>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </>
                  ) : (
                    <>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </>
                  )}
                </select>
              </div>

              <div className="system-users-filter-buttons">
                <button
                  type="button"
                  className="system-users-filter-apply"
                  onClick={() => setShowFilters(false)}
                >
                  Apply Filters
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="system-users-filter-clear"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemUsers;
